import { useCallback, useEffect, useRef } from 'react'
import { BaseQueryFn } from '@reduxjs/toolkit/query'
import {
  TypedUseMutation,
  TypedMutationTrigger,
  TypedUseMutationResult,
} from '@reduxjs/toolkit/query/react'

type SafeBaseQueryFn = BaseQueryFn<unknown, unknown, unknown>

type UseMutationHook<TResult, TArg> = TypedUseMutation<
  TResult,
  TArg,
  SafeBaseQueryFn
>

type Trigger<TResult, TArg> = TypedMutationTrigger<
  TResult,
  TArg,
  SafeBaseQueryFn
>

type Result<TResult, TArg> = TypedUseMutationResult<
  TResult,
  TArg,
  SafeBaseQueryFn
>

type Return<TResult, TArg> = [Trigger<TResult, TArg>, Result<TResult, TArg>]

/**
 * A custom hook that helps to automatically abort previous request when a new one is fired.
 */

export const useAbortableRequest = <TResult, TArg>(
  useMutationHook: UseMutationHook<TResult, TArg>,
): Return<TResult, TArg> => {
  const [trigger, result] = useMutationHook()

  type Trigger = typeof trigger
  type TriggerReturn = ReturnType<Trigger>

  const lastRequestRef = useRef<TriggerReturn>()

  const abortableTrigger = useCallback(
    ((...args: Parameters<Trigger>) => {
      lastRequestRef.current?.abort()
      const req = trigger(...args)
      lastRequestRef.current = req
      return req
    }) as Trigger,
    [trigger],
  )

  useEffect(() => {
    return () => {
      lastRequestRef.current?.abort()
    }
  }, [])

  return [abortableTrigger, result]
}

export default useAbortableRequest
